import { Component, computed, inject, signal } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  ColumnConfig,
  ModalTabConfig,
  FieldType,
  GlobalAction,
  TableActionConfig,
  TableAction,
  GenericTableComponent,
  CustomAction,
  HeaderConfigurationService,
  TableSortConfig,
  SortMode,
  SortDirection,
} from '@solcre-org/core-ui';
import { environment } from '../../../../environments/environment.local';
import { ClientModel } from '../../../shared/models/client.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Country } from '../../../shared/models/country.model';
import { COUNTRIES, getCountryByCode } from '../../../shared/data/countries';

@Component({
  selector: 'app-clients',
  imports: [GenericTableComponent, TranslateModule],
  templateUrl: './clients.html',
})
export class Clients {
  private headerConfigurationService = inject(HeaderConfigurationService);
  private translateService: TranslateService = inject(TranslateService);
  private router: Router = inject(Router);

  endpoint = environment.API.CLIENTS_URI;
  clients = signal<ClientModel[]>([]);
  countries: Country[] = COUNTRIES;

  columns: ColumnConfig<ClientModel>[] = [];

  modalFields = computed<ModalTabConfig<ClientModel>[]>(() => [
    {
      tabId: 'general_data',
      tabLabel: 'clients.general_data',
      fields: [
        {
          label: 'clients.clientName',
          placeholder: 'clients.clientNamePlaceholder',
          key: 'commercialName',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
        },
        {
          label: 'clients.legalName',
          placeholder: 'clients.legalNamePlaceholder',
          key: 'legalName',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
        },
        {
          label: 'clients.fiscalId',
          placeholder: 'clients.fiscalIdPlaceholder',
          key: 'fiscalId',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
        },
        {
          label: 'clients.country',
          placeholder: 'clients.countryPlaceholder',
          key: 'country',
          type: FieldType.SELECT,
          searchable: true,
          options: this.countries,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
          defaultValue: '',
        },
        {
          label: 'clients.address',
          placeholder: 'clients.addressPlaceholder',
          key: 'address',
          type: FieldType.TEXT,
          readonly: false,
        },
      ],
    },
  ]);

  customActions: CustomAction<ClientModel>[] = [
    {
      title: 'Show',
      isExtra: false,
      tooltipPosition: 'top',
      icon: 'icon-arrow-right',
      callback: (row: ClientModel) => {
        this.viewClient(row.id!);
      },
    },
  ];

  globalActions: GlobalAction<ClientModel>[] = [];
  actions: TableActionConfig[] = [
    { action: TableAction.EDIT, isExtra: false },
    { action: TableAction.DELETE, isExtra: true },
    { action: TableAction.SELECT, isExtra: false },
    { action: TableAction.CREATE, isExtra: false },
  ];

  sortConfig = signal<TableSortConfig>({
    mode: SortMode.SERVER,
    multiColumn: false,
    serverSortParam: 'order',
    defaultSort: [
      {
        key: 'commercialName',
        direction: SortDirection.ASC,
      },
    ],
  });

  ngOnInit() {
    this.headerConfigurationService.setEndpointHeaders(this.endpoint, {
      patch: [{ 'Content-Type': 'application/merge-patch+json' }],
    });
  }

  ngAfterViewInit(): void {
    this.columns = [
      {
        label: 'clients.clientName',
        key: 'commercialName',
        sortable: true,
        sortKey: 'commercialName',
      },
      {
        label: 'clients.country',
        key: 'country',
        sortable: true,
        sortKey: 'country',
        format: (value: string) => {
          const country = this.getCountryByName(value);
          return country?.label || value;
        },
      },
      {
        label: 'clients.mainContact',
        key: 'legalName',
        sortable: true,
        sortKey: 'legalName',
      },
      {
        label: 'clients.address',
        key: 'address',
      },
    ];
  }

  clientsModelFactory = (data: Partial<ClientModel>): ClientModel => {
    return new ClientModel().fromJSON(data);
  };

  private viewClient(id: number): void {
    this.router.navigate([`app/client/${id}`]);
  }

  getCountryByName(name: string): Country | undefined {
    return getCountryByCode(name);
  }
}
